/**
 * Discussions asynchrones tuteur ↔ élève (migrations 0265-0267) — types
 * partagés par les helpers purs et les hooks de `lib/discussions/`.
 */

export type DiscussionSubjectScope = 'exercise' | 'submission' | 'chapter' | 'free';

export interface DiscussionRow {
  id: string;
  tutorId: string;
  tutorName: string | null;
  studentId: string;
  studentName: string | null;
  /** Avatars joints (optionnels : absents des lignes construites à la main). */
  tutorAvatarUrl?: string | null;
  studentAvatarUrl?: string | null;
  title: string;
  subjectScope: DiscussionSubjectScope;
  exerciseId: string | null;
  submissionId: string | null;
  chapterId: string | null;
  createdBy: string;
  createdAt: string;
  lastMessageAt: string;
  lastReadAtTutor: string | null;
  lastReadAtStudent: string | null;
}

/** Ligne `discussions` (snake_case PostgREST) avec ses noms joints. */
export interface RawDiscussionRow {
  id: string;
  tutor_id: string;
  student_id: string;
  title: string;
  subject_scope: DiscussionSubjectScope;
  exercise_id: string | null;
  submission_id: string | null;
  chapter_id: string | null;
  created_by: string;
  created_at: string;
  last_message_at: string;
  last_read_at_tutor: string | null;
  last_read_at_student: string | null;
  tutor?: { display_name: string | null; avatar_url?: string | null } | null;
  student?: { display_name: string | null; avatar_url?: string | null } | null;
}

export function discussionFromRow(row: RawDiscussionRow): DiscussionRow {
  return {
    id: row.id,
    tutorId: row.tutor_id,
    tutorName: row.tutor?.display_name ?? null,
    studentId: row.student_id,
    studentName: row.student?.display_name ?? null,
    tutorAvatarUrl: row.tutor?.avatar_url ?? null,
    studentAvatarUrl: row.student?.avatar_url ?? null,
    title: row.title,
    subjectScope: row.subject_scope,
    exerciseId: row.exercise_id,
    submissionId: row.submission_id,
    chapterId: row.chapter_id,
    createdBy: row.created_by,
    createdAt: row.created_at,
    lastMessageAt: row.last_message_at,
    lastReadAtTutor: row.last_read_at_tutor,
    lastReadAtStudent: row.last_read_at_student,
  };
}

/** L'AUTRE participant d'une discussion (nom + avatar), vu depuis `myRole`. */
export function discussionOtherParty(
  discussion: Pick<
    DiscussionRow,
    'tutorName' | 'studentName' | 'tutorAvatarUrl' | 'studentAvatarUrl'
  >,
  myRole: 'tutor' | 'student',
): { name: string; avatarUrl: string | null } {
  return myRole === 'tutor'
    ? { name: discussion.studentName ?? 'Contact', avatarUrl: discussion.studentAvatarUrl ?? null }
    : { name: discussion.tutorName ?? 'Contact', avatarUrl: discussion.tutorAvatarUrl ?? null };
}

/** Un message asynchrone n'a jamais 'voice' (dictée en direct, propre à une salle visio). */
export type DiscussionMessageSource = 'typed' | 'file';

export interface RawDiscussionMessageRow {
  id: string;
  discussion_id: string;
  sender_id: string;
  body: string;
  source: DiscussionMessageSource;
  document_id: string | null;
  hidden_at: string | null;
  created_at: string;
}

/** Ligne `visio_room_messages` — même forme que le chat visio, `source` peut valoir 'voice'. */
export interface RawRoomMessageRow {
  id: string;
  room_id: string;
  sender_id: string;
  body: string;
  source: 'typed' | 'voice' | 'file';
  document_id: string | null;
  hidden_at: string | null;
  created_at: string;
}

/**
 * Message normalisé pour l'affichage du fil, quelle que soit sa source
 * d'origine (discussion asynchrone, ou salle visio rattachée). `id` est
 * préfixé par origine (`disc:`/`room:`) : unicité garantie pour la clé React,
 * même si un id de message de discussion et un id de message visio coïncidaient.
 */
export interface DiscussionTimelineMessage {
  /** Préfixé par origine (`disc:`/`room:`) — clé React, jamais de collision. */
  id: string;
  /** id NU (sans préfixe) — celui à passer aux RPC de masquage. */
  rawId: string;
  origin: 'discussion' | 'room';
  /** Non-null seulement si `origin === 'room'`. */
  roomId: string | null;
  senderId: string;
  body: string;
  source: 'typed' | 'voice' | 'file';
  documentId: string | null;
  hiddenAt: string | null;
  createdAt: string;
}

export type DiscussionErrorCode =
  | 'invalid_title'
  | 'invalid_subject'
  | 'not_authorized'
  | 'invalid_body'
  | 'create_failed'
  | 'send_failed'
  | 'list_failed'
  | 'call_failed'
  | 'blocked'
  | 'moderated';

/** Erreur typée : `message` est déjà en français, affichable telle quelle. */
export class DiscussionError extends Error {
  constructor(
    public readonly code: DiscussionErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'DiscussionError';
  }
}

/**
 * Erreur serveur d'écriture dans le fil → erreur typée (migration 0604) :
 * `blocked` = l'un des deux a bloqué l'autre ; `moderated` = message masqué
 * par la modération, impossible à rétablir. Sinon, l'erreur générique. PUR.
 */
export function discussionWriteError(
  serverMessage: string | undefined | null,
  fallback: string,
): DiscussionError {
  const m = serverMessage ?? '';
  if (/\bblocked\b/.test(m)) {
    return new DiscussionError('blocked', 'Tu ne peux plus écrire dans cette discussion.');
  }
  if (/\bmoderated\b/.test(m)) {
    return new DiscussionError(
      'moderated',
      'Ce message a été retiré par la modération : il ne peut pas être rétabli.',
    );
  }
  return new DiscussionError('send_failed', fallback);
}
